/* NYXO — animations.js
   Loaded only when "Enable animations" is on. Adds html.motion-ok, which the
   CSS uses as the opt-in switch. Without this file (or with reduced motion),
   every element renders fully visible — motion is purely additive. */
(function () {
  'use strict';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) return;
  if (!(window.NYXO && window.NYXO.animations)) return;

  var html = document.documentElement;
  html.classList.add('motion-ok');
  reduced.addEventListener('change', function (e) {
    if (e.matches) html.classList.remove('motion-ok');
  });

  var hasIO = 'IntersectionObserver' in window;

  /* ---- Word-mask splitter: <el data-split>Text</el> -> masked word spans ---- */
  document.querySelectorAll('[data-split]').forEach(function (el) {
    var text = (el.textContent || '').trim();
    if (!text) return;
    var frag = document.createDocumentFragment();
    text.split(/\s+/).forEach(function (word, i) {
      var mask = document.createElement('span');
      mask.className = 'w-mask';
      var inner = document.createElement('span');
      inner.className = 'w-inner';
      inner.style.setProperty('--wi', i);
      inner.textContent = word;
      mask.appendChild(inner);
      frag.appendChild(mask);
    });
    el.textContent = '';
    el.appendChild(frag);
    el.classList.add('is-split');
  });

  /* ---- Reveals: early trigger + fast-scroll catch-up ---- */
  var revealTargets = document.querySelectorAll('[data-reveal], [data-reveal-group], [data-split], .footer__statement');
  if (hasIO) {
    var vh = function () { return window.innerHeight || 1; };
    var revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // If the element is already well inside (or above) the viewport, the
        // user is moving fast — snap in instead of playing the full entrance.
        if (entry.boundingClientRect.top < vh() * 0.35) entry.target.classList.add('is-instant');
        entry.target.classList.add('is-in');
        revealIO.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -4% 0px', threshold: 0 });
    revealTargets.forEach(function (el) {
      // Anything already above the fold at init renders immediately.
      if (el.getBoundingClientRect().top < vh() * 0.9) {
        el.classList.add('is-in', 'is-instant');
      } else {
        revealIO.observe(el);
      }
    });
  } else {
    revealTargets.forEach(function (el) { el.classList.add('is-in'); });
  }

  /* ---- Hero intro ---- */
  var hero = document.querySelector('[data-hero]');
  if (hero) {
    window.requestAnimationFrame(function () {
      window.setTimeout(function () { hero.classList.add('is-loaded'); }, 80);
    });
  }

  /* ---- Section state effects ---- */
  if (hasIO) {
    var fx = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var el = entry.target;
        if (el.hasAttribute('data-anc')) {
          if (entry.isIntersecting) el.classList.add('is-quiet');
          else el.classList.remove('is-quiet');
        } else if (el.hasAttribute('data-gaming')) {
          if (entry.isIntersecting) {
            el.classList.remove('is-frozen');
            window.clearTimeout(el._freeze);
            el._freeze = window.setTimeout(function () { el.classList.add('is-frozen'); }, 1400);
          } else {
            window.clearTimeout(el._freeze);
            el.classList.remove('is-frozen');
          }
        } else if (el.hasAttribute('data-battery')) {
          el.classList.toggle('is-on', entry.isIntersecting);
        } else {
          el.classList.toggle('is-in-view', entry.isIntersecting);
        }
      });
    }, { threshold: 0.45 });
    document.querySelectorAll('[data-anc], [data-gaming], [data-battery], .feature--fx-bass, .feature--fx-clarity').forEach(function (el) {
      fx.observe(el);
    });
  }

  /* ---- Frequency canvas waveform ---- */
  var canvas = document.querySelector('[data-freq-canvas]');
  if (canvas && canvas.getContext) {
    var section = canvas.closest('[data-frequency]') || canvas.parentElement;
    var fallback = document.querySelector('[data-freq-fallback]');
    var ctx = canvas.getContext('2d');
    var running = false;
    var t = 0;
    var amp = 0.4;
    var accent = getComputedStyle(html).getPropertyValue('--accent').trim() || '#8B7CFF';
    var dim = 'rgba(245,245,242,0.22)';

    function size() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, rect.width * dpr);
      canvas.height = Math.max(1, rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function wave(freq, phase, amplitude, color, width) {
      var rect = canvas.getBoundingClientRect();
      var w = rect.width, h = rect.height, mid = h / 2;
      ctx.beginPath();
      for (var x = 0; x <= w; x += 3) {
        var env = Math.sin((x / w) * Math.PI);
        var y = mid + Math.sin(x * freq + phase) * amplitude * env * (h * 0.34);
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.stroke();
    }

    function frame() {
      if (!running) return;
      var rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      t += 0.016;
      wave(0.014, t * 1.4, amp * 0.55, dim, 1);
      wave(0.021, -t * 1.1, amp * 0.8, dim, 1);
      wave(0.03, t * 2.1, amp, accent, 1.5);
      window.requestAnimationFrame(frame);
    }

    function scrollAmp() {
      var rect = (section || canvas).getBoundingClientRect();
      var vh = window.innerHeight || 1;
      var progress = 1 - Math.min(Math.max((rect.top + rect.height * 0.5) / vh, 0), 1);
      amp = 0.35 + progress * 0.75;
    }

    if (hasIO) {
      var canvasIO = new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        if (visible && !running) {
          running = true;
          size();
          if (fallback) fallback.style.display = 'none';
          canvas.style.display = 'block';
          frame();
        } else if (!visible) {
          running = false;
        }
      }, { threshold: 0.1 });
      canvasIO.observe(canvas);
      window.addEventListener('resize', size);
      window.addEventListener('scroll', scrollAmp, { passive: true });
      scrollAmp();
    }
  }

  /* ---- Parallax ---- */
  var parallaxEls = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
  if (parallaxEls.length) {
    var pTicking = false;
    var applyParallax = function () {
      parallaxEls.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.08;
        var rect = el.getBoundingClientRect();
        var vh = window.innerHeight || 1;
        if (rect.bottom < 0 || rect.top > vh) return;
        var offset = (rect.top + rect.height / 2 - vh / 2) * speed;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      pTicking = false;
    };
    window.addEventListener('scroll', function () {
      if (!pTicking) { pTicking = true; window.requestAnimationFrame(applyParallax); }
    }, { passive: true });
    applyParallax();
  }

  /* ---- Magnetic buttons (desktop pointer only) ---- */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.querySelectorAll('[data-magnetic]').forEach(function (el) {
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var x = (e.clientX - rect.left - rect.width / 2) * 0.18;
        var y = (e.clientY - rect.top - rect.height / 2) * 0.22;
        el.style.setProperty('--mx', x.toFixed(1) + 'px');
        el.style.setProperty('--my', y.toFixed(1) + 'px');
      });
      el.addEventListener('mouseleave', function () {
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      });
    });
  }

  /* ---- Frequency rail: scroll progress + section index ---- */
  var rail = document.querySelector('[data-freq-rail]');
  if (rail) {
    var ticks = Array.prototype.slice.call(rail.querySelectorAll('.freq-rail__tick'));
    var indexEl = rail.querySelector('[data-freq-index]');
    var sections = Array.prototype.slice.call(document.querySelectorAll('[data-section-index]'));
    var rTicking = false;
    var paintRail = function () {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      var p = max > 0 ? window.scrollY / max : 0;
      var lit = Math.round(p * ticks.length);
      ticks.forEach(function (tick, i) { tick.classList.toggle('is-on', i < lit); });
      if (indexEl && sections.length) {
        var current = '01';
        for (var i = 0; i < sections.length; i++) {
          var rect = sections[i].getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.5) current = sections[i].getAttribute('data-section-index') || current;
        }
        indexEl.textContent = current;
      }
      rTicking = false;
    };
    window.addEventListener('scroll', function () {
      if (!rTicking) { rTicking = true; window.requestAnimationFrame(paintRail); }
    }, { passive: true });
    paintRail();
  }
})();
